import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import api from '../../services/api';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const getWeekdayName = (week) => {
  if (!week) return '-';
  const name = typeof week === 'string' ? week : week.name || '';
  if (!name) return '-';
  return name.charAt(0).toUpperCase() + name.slice(1);
};


const getShiftFromTime = (time) => {
  if (!time || typeof time !== 'string') return 'Fora do turno';
  const parts = time.split(':');
  const hour = Number(parts[0]);
  if (Number.isNaN(hour)) return 'Fora do turno';
  if (hour >= 6 && hour < 12) return 'Manhã';
  if (hour >= 12 && hour < 18) return 'Tarde';
  if (hour >= 18 && hour < 24) return 'Noite';
  return 'Fora do turno';
};

function HomeNutricionista({ setIsLoggedIn }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settingsReport, setSettingsReport] = useState([]);
  const [summaryReport, setSummaryReport] = useState([]);
  const [polos, setPolos] = useState([]);
  const [selectedPolo, setSelectedPolo] = useState(() => localStorage.getItem('selectedPoloId') || '');
  const [reportLoading, setReportLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const handleLogout = async () => {
    try {
      await api.post('/logout');
    } catch (e) {
      // continuar mesmo se ocorrer erro no logout do servidor
      console.error('Erro no logout:', e);
    }

    Object.keys(Cookies.get()).forEach(cookieName => {
      Cookies.remove(cookieName);
    });
    localStorage.clear();
    sessionStorage.clear();
    if (setIsLoggedIn) {
      setIsLoggedIn(false);
    }
    navigate('/');
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchPolos = async () => {
      try {
        const res = await api.get('/user/polos-by-user');
        const list = Array.isArray(res.data) ? res.data : (res.data?.polos || []);
        setPolos(list);

        const storedPolo = localStorage.getItem('selectedPoloId');
        if (!storedPolo && list.length > 0) {
          const initial = list[0]?.id?.toString() || '';
          setSelectedPolo(initial);
          localStorage.setItem('selectedPoloId', initial);
        }
      } catch (err) {
        console.error('Erro ao buscar polos:', err);
      }
    };
    fetchPolos();
  }, []);

  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      setError(null);

      const poloId = selectedPolo || localStorage.getItem('selectedPoloId');

      try {
        const settingsParams = poloId ? { polo_id: poloId } : {};
        const settingsRes = await api.get('/automated-attendance-configs/group-by-professor', { params: settingsParams });
        setSettingsReport(settingsRes.data?.data || []);
        setSummaryReport(settingsRes.data?.summary || []);
      } catch (err) {
        console.error('Erro ao carregar relatórios:', err);
        setError('Erro ao carregar relatórios.');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [selectedPolo]);

  const buildSummary = () => {
    if (summaryReport && summaryReport.length > 0) {
      return summaryReport;
    }

    const summary = {};

    settingsReport.forEach((group) => {
      const items = Array.isArray(group?.attendance?.data)
        ? group.attendance.data
        : (Array.isArray(group?.items) ? group.items : []);

      items.forEach((item) => {
        const shift = getShiftFromTime(item.start_time || '');
        const count = item.course && (item.course.students_count ?? (Array.isArray(item.course.students) ? item.course.students.length : 0))
          ? Number(item.course.students_count ?? (Array.isArray(item.course.students) ? item.course.students.length : 0))
          : 0;

        const weekNames = Array.isArray(item.weeks) && item.weeks.length
          ? item.weeks.map(getWeekdayName)
          : ['-'];

        weekNames.forEach((weekday) => {
          const key = `${weekday}|${shift}`;
          summary[key] = summary[key] || { weekday, shift, students: 0 };
          summary[key].students += count;
        });
      });
    });

    return Object.values(summary).sort((a, b) => {
      const order = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo', '-'];
      const aDay = order.indexOf(a.weekday) >= 0 ? order.indexOf(a.weekday) : 999;
      const bDay = order.indexOf(b.weekday) >= 0 ? order.indexOf(b.weekday) : 999;
      if (aDay !== bDay) return aDay - bDay;
      const shiftOrder = ['Manhã', 'Tarde', 'Noite', 'Fora do turno'];
      return shiftOrder.indexOf(a.shift) - shiftOrder.indexOf(b.shift);
    });
  };

  const handleGenerateReport = () => {
    setReportLoading(true);
    try {
      const doc = new jsPDF('landscape');
      doc.setFontSize(14);
      doc.text('Relatório de Turma por Professor', 14, 15);
      doc.setFontSize(10);
      const dt = new Date().toLocaleString('pt-BR');
      doc.text(`Gerado em: ${dt}`, 14, 22);
      doc.text(`Polo: ${polos.find((p) => p.id?.toString() === selectedPolo)?.name || 'Todos'}`, 14, 28);

      const settingsHead = [['Professor', 'Turma', 'Dias', 'Início', 'Fim', 'Alunos']];
      const settingsBody = settingsReport.flatMap((group) => {
        const professorName = group.professor?.name || 'Sem Professor';
        const items = Array.isArray(group?.attendance?.data)
          ? group.attendance.data
          : (Array.isArray(group?.items) ? group.items : []);

        if (items.length === 0) return [[professorName, '-', '-', '-', '-', '-']];

        return items.map((item) => {
          const studentCount = item.course && (item.course.students_count ?? (Array.isArray(item.course.students) ? item.course.students.length : 0))
            ? Number(item.course.students_count ?? (Array.isArray(item.course.students) ? item.course.students.length : 0))
            : 0;

          return [
            professorName,
            item.course?.name || '-',
            Array.isArray(item.weeks) && item.weeks.length
              ? item.weeks.map(getWeekdayName).join(', ')
              : '-',
            item.start_time || '-',
            item.end_time || '-',
            studentCount,
          ];
        });
      });

      doc.autoTable({
        head: settingsHead,
        body: settingsBody,
        startY: 36,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [38, 166, 154] },
      });

      const summaryData = buildSummary();
      const finalY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 12 : 60;
      doc.setFontSize(12);
      doc.text('Resumo de quantidade de alunos por dia/turno', 14, finalY);

      const summaryHead = [['Dia da semana', 'Turno', 'Alunos']];
      const summaryBody = summaryData.map((item) => [item.weekday, item.shift, item.students]);

      doc.autoTable({
        head: summaryHead,
        body: summaryBody,
        startY: finalY + 5,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [77, 182, 172] },
      });

      doc.save('relatorio-turma-por-professor.pdf');
    } catch (err) {
      console.error('Erro ao gerar relatório em PDF:', err);
      alert('Erro ao gerar relatório PDF.');
    } finally {
      setReportLoading(false);
    }
  };


  const mainContainerStyle = {
    minHeight: '100vh',
    background: '#f8fafc',
    padding: isMobile ? '16px 8px 24px' : 0,
  };

  const contentWrapperStyle = {
    maxWidth: isMobile ? '100%' : 1200,
    margin: '0 auto',
    padding: isMobile ? '16px 10px' : '32px 16px',
  };

  const filterBarStyle = {
    marginBottom: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    flexWrap: 'wrap',
    flexDirection: isMobile ? 'column' : 'row',
  };

  const filterItemStyle = {
    width: isMobile ? '100%' : 'auto',
  };

  const tableWrapperStyle = {
    maxHeight: 600,
    overflowY: 'auto',
    overflowX: 'auto',
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: isMobile ? 12 : 14,
    minWidth: 780,
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return <div style={{ color: 'red', textAlign: 'center', marginTop: 40 }}>{error}</div>;
  }

  return (
    <div style={mainContainerStyle}>
      <div style={contentWrapperStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isMobile ? 'column' : 'row', gap: 12, marginBottom: 16 }}>
          <h1 style={{ fontSize: isMobile ? 24 : 32, fontWeight: 700, color: '#374151', marginBottom: isMobile ? 8 : 0, textAlign: 'center' }}>
            Painel da Nutricionista
          </h1>
          <button
            onClick={handleLogout}
            style={{
              background: '#dc3545',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: isMobile ? '10px 14px' : '10px 18px',
              cursor: 'pointer',
              width: isMobile ? '100%' : 'auto',
              fontWeight: 600,
              fontSize: 14,
            }}
          >
            Logout
          </button>
        </div>
        <p style={{ color: '#607d8b', fontSize: 18, textAlign: 'center', marginBottom: 32 }}>
          Bem-vinda! Aqui você pode visualizar o relatório de turma por professor e o resumo de chamada automática.
        </p>

        <div style={filterBarStyle}>
          <label style={{ fontWeight: 600, color: '#374151', width: isMobile ? '100%' : 'auto', textAlign: isMobile ? 'center' : 'left' }} htmlFor="polo-select">
            Polo:
          </label>
          <select
            id="polo-select"
            value={selectedPolo}
            onChange={(e) => {
              setSelectedPolo(e.target.value);
              localStorage.setItem('selectedPoloId', e.target.value);
            }}
            style={{ ...filterItemStyle, padding: '8px 10px', borderRadius: 6, border: '1px solid #cbd5e1' }}
          >
            <option value="">— Selecionar polo —</option>
            {polos.map((polo) => (
              <option key={polo.id} value={polo.id}>
                {polo.name}
              </option>
            ))}
          </select>

          <button
            onClick={handleGenerateReport}
            disabled={reportLoading}
            style={{
              background: '#0ea5e9',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              padding: isMobile ? '12px 14px' : '10px 18px',
              cursor: 'pointer',
              width: isMobile ? '100%' : 'auto',
            }}
          >
            {reportLoading ? 'Gerando relatório...' : 'Gerar Relatório (PDF)'}
          </button>

        </div>

        <div style={{ background: 'white', borderRadius: 16, boxShadow: '0 2px 16px rgba(44,62,80,0.08)', padding: 24 }}>
          <h2 style={{ color: '#374151', fontSize: 24, fontWeight: 600, marginBottom: 16 }}>
            Relatório de Turma por Professor
          </h2>
          {(!settingsReport || settingsReport.length === 0) ? (
            <div style={{ color: '#b0bec5' }}>Nenhum dado encontrado.</div>
          ) : (
            <div style={tableWrapperStyle}>
              {settingsReport.map((group, idx) => {
                const professorName = group.professor?.name || 'Sem professor';
                const items = Array.isArray(group?.attendance?.data) ? group.attendance.data : (Array.isArray(group?.items) ? group.items : []);

                return (
                  <div key={idx} style={{ marginBottom: 16, border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
                    <h3 style={{ margin: '0 0 8px', color: '#1f2937' }}>{professorName}</h3>
                    {items.length === 0 ? (
                      <div style={{ color: '#9ca3af' }}>Nenhuma configuração encontrada para este professor.</div>
                    ) : (
                      <table style={tableStyle}>
                        <thead>
                          <tr style={{ background: '#e0f2fe' }}>
                            <th style={{ padding: 6, borderBottom: '1px solid #d1d5db' }}>Turma</th>
                            <th style={{ padding: 6, borderBottom: '1px solid #d1d5db' }}>Dias</th>
                            <th style={{ padding: 6, borderBottom: '1px solid #d1d5db' }}>Início</th>
                            <th style={{ padding: 6, borderBottom: '1px solid #d1d5db' }}>Fim</th>
                            <th style={{ padding: 6, borderBottom: '1px solid #d1d5db' }}>Alunos</th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((conf, cidx) => (
                            <tr key={cidx} style={{ borderBottom: '1px solid #f3f4f6' }}>
                              <td style={{ padding: 6 }}>{conf.course?.name || '-'}</td>
                              <td style={{ padding: 6 }}>{(conf.weeks || []).map((w) => getWeekdayName(w)).join(', ') || '-'}</td>
                              <td style={{ padding: 6 }}>{conf.start_time || '-'}</td>
                              <td style={{ padding: 6 }}>{conf.end_time || '-'}</td>
                              <td style={{ padding: 6 }}>{conf.course?.students_count ?? (Array.isArray(conf.course?.students) ? conf.course.students.length : '-')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ marginTop: 40, color: '#b0bec5', fontSize: 14, textAlign: 'center' }}>
          <span role="img" aria-label="saude">💚</span> Saúde e bem-estar dos alunos em primeiro lugar!
        </div>
      </div>
    </div>
  );
}

export default HomeNutricionista;
